import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Card, CardContent, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Divider, MenuItem, TextField
} from '@mui/material';
import {
  TrendingUp as AnalyticsIcon, Download as CSVIcon, PictureAsPdf as PDFIcon,
  People as ChurnIcon, School as TeacherIcon, Domain as RoomIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

export const ReportsPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedYear, setSelectedYear] = useState('2026');

  const students = academyStore.getStudents();
  const teachers = academyStore.getTeachers();
  const rooms = academyStore.getRooms();
  const groups = academyStore.getGroups();
  const attendances = academyStore.getAttendances();
  const payments = academyStore.getPayments();
  const groupStudents = academyStore.getGroupStudents();

  // 1. Teacher Performance (Attendance Rates)
  const teacherPerformance = teachers.map(teacher => {
    const teacherSessions = academyStore.getSessions().filter(s => s.teacher_id === teacher.id);
    const sessionIds = teacherSessions.map(s => s.id);
    const teacherAtts = attendances.filter(a => sessionIds.includes(a.session_id));
    const totalCount = teacherAtts.length;
    const presentCount = teacherAtts.filter(a => a.status === 'Present' || a.status === 'Late').length;
    const rate = totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 100;

    return {
      teacher,
      sessionsCount: teacherSessions.length,
      attendanceRate: rate
    };
  }).sort((a, b) => b.attendanceRate - a.attendanceRate);

  // 2. Room Utilization Rates
  const roomAnalytics = rooms.map(room => {
    const util = academyStore.getRoomUtilizationPercentage(room.id);
    return { room, util };
  }).sort((a, b) => b.util - a.util);

  // 3. Churn Report (Students Dropped or No Attendance)
  const droppedEnrollments = groupStudents.filter(gs => gs.status === 'Dropped');
  const churnedStudents = droppedEnrollments.map(gs => {
    const student = students.find(s => s.id === gs.student_id);
    const group = groups.find(g => g.id === gs.group_id);
    return { student, group, enrollmentDate: gs.enrollment_date };
  });

  // Export to CSV Handler
  const exportToCSV = () => {
    const csvRows = [
      ['Report Type', 'Academy Analytics Summary'],
      ['Export Date', new Date().toISOString().split('T')[0]],
      [''],
      ['Teacher Name', 'Specialization', 'Attendance Rate (%)'],
      ...teacherPerformance.map(tp => [tp.teacher.full_name, tp.teacher.specialization, `${tp.attendanceRate}%`]),
      [''],
      ['Room Number', 'Capacity', 'Utilization (%)'],
      ...roomAnalytics.map(ra => [ra.room.room_number, ra.room.capacity, `${ra.util}%`])
    ];

    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.map(e => e.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Academy_Analytics_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ height: '100%', minHeight: 0, overflowY: 'auto', pb: 4 }}>
      {/* Header Bar */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: 'background.paper', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AnalyticsIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">{t('academy.reports.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('academy.reports.subtitle')}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField select size="small" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} sx={{ width: 100 }}>
            <MenuItem value="2026">2026</MenuItem>
            <MenuItem value="2025">2025</MenuItem>
          </TextField>

          <Button variant="outlined" color="primary" startIcon={<CSVIcon />} onClick={exportToCSV}>
            {t('academy.reports.export_csv')}
          </Button>
          <Button variant="contained" color="primary" startIcon={<PDFIcon />} onClick={() => window.print()}>
            {t('academy.reports.print_pdf')}
          </Button>
        </Box>
      </Paper>

      {/* Analytics Dashboard Grid */}
      <Grid container spacing={3}>
        {/* Teacher Performance Ranking */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TeacherIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">{t('academy.reports.teacher_perf_title')}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {t('academy.reports.teacher_perf_sub')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {teacherPerformance.map((item, idx) => (
                <Box key={item.teacher.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      #{idx + 1} {item.teacher.full_name} ({item.teacher.specialization})
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {t('academy.reports.rate_label', item.attendanceRate)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.attendanceRate}
                    color={item.attendanceRate >= 80 ? 'success' : item.attendanceRate >= 60 ? 'warning' : 'error'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Room Utilization Pie / Bar Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RoomIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">{t('academy.reports.room_util_title')}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {t('academy.reports.room_util_sub')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {roomAnalytics.map(ra => (
                <Box key={ra.room.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t('academy.rooms.room_prefix', ra.room.room_number)} ({ra.room.capacity} {t('academy.reports.capacity_short')})
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="secondary">
                      {t('academy.reports.booked_label', ra.util)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={ra.util}
                    color={ra.util > 75 ? 'primary' : 'info'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Churn Report Table */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ChurnIcon color="error" />
              <Typography variant="h6" fontWeight="bold">{t('academy.reports.churn_title')}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {t('academy.reports.churn_sub')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {churnedStudents.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'success.main' }}>
                <Typography variant="subtitle2" fontWeight="bold">{t('academy.reports.zero_churn')}</Typography>
                <Typography variant="caption">{t('academy.reports.zero_churn_sub')}</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'action.hover' }}>
                    <TableRow>
                      <TableCell>{t('academy.reports.table.student')}</TableCell>
                      <TableCell>{t('academy.reports.table.group')}</TableCell>
                      <TableCell>{t('academy.reports.table.enrollment_date')}</TableCell>
                      <TableCell>{t('academy.reports.table.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {churnedStudents.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{item.student?.first_name} {item.student?.last_name}</TableCell>
                        <TableCell>{item.group?.name}</TableCell>
                        <TableCell>{item.enrollmentDate}</TableCell>
                        <TableCell><Chip label={t('academy.students.status.dropped')} color="error" size="small" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportsPage;
